import { useState, useEffect, useMemo, useCallback, Component, ErrorInfo, ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useWallet } from '@solana/wallet-adapter-react'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { ArrowLeft, TrendingUp, Coins, Calendar, DollarSign, AlertTriangle, LogIn } from 'lucide-react'
import InvestmentModal from '../components/investment/InvestmentModal'
import AuthModal from '../components/auth/AuthModal'
import { useAuth } from '../hooks/useAuth'
// getTierDisplayName removed - vaults no longer have tier name
import { useConfig } from '../contexts/ConfigContext'
import { useCountdown, getVaultLaunchDate } from '../hooks/useCountdown'

// Custom hook for debouncing (1 second delay)
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Error Boundary to prevent full page crashes
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class CalculatorErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Calculator error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // Note: We can't use useTranslation in class components, strings remain in English
      return this.props.fallback || (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-center">
          <p className="text-red-400 mb-2">Calculator error occurred</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function VaultDetailPage() {
  const { t } = useTranslation(['vaults', 'common'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { connected } = useWallet()
  const { user } = useAuth()
  const config = useConfig()
  // Check BOTH token exists AND user data loaded (token is valid)
  const isAuthenticated = api.isAuthenticated() && !!user
  
  // Get vault launch date from environment variable (must include timezone offset)
  // Memoize to prevent re-parsing on every render
  const launchDate = useMemo(() => getVaultLaunchDate(), [])
  const countdown = useCountdown(launchDate)
  // Vault purchasing is enabled if:
  // - No launch date is set (no countdown required), OR
  // - Launch date is set AND countdown has expired
  const isVaultPurchasingEnabled = launchDate === null || countdown.isExpired
  
  const [usdtAmount, setUsdtAmount] = useState<string>('')
  const [boostToken, setBoostToken] = useState<'LAIKA' | 'TAKARA'>('LAIKA')
  const [laikaAmount, setLaikaAmount] = useState<number>(0)
  const [takaraAmount, setTakaraAmount] = useState<number>(0)  // Separate state for TAKARA boost
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)

  // Debounce USDT amount - wait 1 second after user stops typing
  const debouncedUsdtAmount = useDebounce(usdtAmount, 1000)
  // Debounce boost amounts - wait 500ms after user stops typing
  const debouncedLaikaAmount = useDebounce(laikaAmount, 500)
  const debouncedTakaraAmount = useDebounce(takaraAmount, 500)

  const { data: vaultResponse, isLoading: vaultLoading } = useQuery({
    queryKey: ['vault', id],
    queryFn: () => api.getVaultById(id!),
    enabled: !!id,
    refetchInterval: 60000, // Refetch every minute to update countdown
  })

  const vault = vaultResponse?.data?.vault

  // Fetch LAIKA price on page load (v2.7) - refetch when laikaAmount changes
  const { data: laikaPriceResponse, isFetching: laikaPriceFetching, refetch: refetchLaikaPrice } = useQuery({
    queryKey: ['laikaPrice'],
    queryFn: () => api.getLaikaPrice(),
    staleTime: 5 * 60 * 1000, // 5 minutes - increased to reduce requests
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })

  // Fetch TAKARA dynamic price (from admin stats endpoint)
  const { data: takaraPriceResponse } = useQuery({
    queryKey: ['takaraPrice'],
    queryFn: () => api.getTakaraPrice(),
    staleTime: 5 * 60 * 1000, // 5 minutes - increased to reduce requests
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })

  const takaraPrice = takaraPriceResponse?.data?.price ?? 0.001506

  // Handler to update LAIKA boost amount
  // Price is already cached and auto-refreshed by useQuery
  const handleLaikaAmountChange = (newAmount: number) => {
    setLaikaAmount(newAmount)
  }

  // Handler to update TAKARA boost amount
  const handleTakaraAmountChange = (newAmount: number) => {
    setTakaraAmount(newAmount)
  }

  // Get current boost amount based on selected token
  const currentBoostAmount = boostToken === 'TAKARA' ? takaraAmount : laikaAmount
  const handleBoostAmountChange = boostToken === 'TAKARA' ? handleTakaraAmountChange : handleLaikaAmountChange

  // Safe parse function to prevent NaN
  const safeParseFloat = useCallback((value: string): number => {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  }, [])

  const parsedUsdtAmount = useMemo(() => safeParseFloat(debouncedUsdtAmount), [debouncedUsdtAmount, safeParseFloat])
  // Current parsed amount for immediate use (non-debounced)
  const currentParsedAmount = useMemo(() => safeParseFloat(usdtAmount), [usdtAmount, safeParseFloat])

  const { data: calculationResponse, isLoading: calculating, error: calculateError } = useQuery({
    queryKey: ['calculate', id, debouncedUsdtAmount, debouncedLaikaAmount, debouncedTakaraAmount, boostToken],
    queryFn: () =>
      api.calculateInvestment(id!, {
        usdtAmount: parsedUsdtAmount,
        laikaAmount: boostToken === 'LAIKA' && debouncedLaikaAmount > 0 ? debouncedLaikaAmount : undefined,
        takaraAmount: boostToken === 'TAKARA' && debouncedTakaraAmount > 0 ? debouncedTakaraAmount : undefined,
      }),
    // Only fetch when: vault loaded, valid amount, AND amount >= minInvestment
    enabled: !!id && !!vault && !!debouncedUsdtAmount && parsedUsdtAmount >= (vault?.minInvestment || 0),
    retry: false, // Don't retry on validation errors
    staleTime: 5000, // Cache for 5 seconds to reduce API calls while user types
  })

  const calculation = calculationResponse?.data

  // Calculate max boost for LAIKA and TAKARA
  // LAIKA: market value 1:1 (no multiplier)
  // TAKARA: v3.0 formula - fullBoostCostUSD = usdtInvested × (boostRange / 100)
  const { laikaToUsdtRate, maxLaikaBoostUSD, maxLaikaMarketValueUSD, maxLaikaBoost, maxTakaraBoost } = useMemo(() => {
    // @ts-ignore - Type definitions need updating
    const laikaRate = calculation?.investment?.laikaPrice || laikaPriceResponse?.data?.price || 0.0000007
    const currentUsdtAmount = safeParseFloat(usdtAmount)

    // LAIKA: 20% of USDT, market value 1:1
    const boostUSD = currentUsdtAmount > 0 ? currentUsdtAmount * 0.25 : 0
    const marketValueUSD = boostUSD // Market value = boost value (1:1)
    const maxLaika = laikaRate > 0 ? marketValueUSD / laikaRate : 0

    // TAKARA v3.0: Use takaraBoostInfo from API if available
    // @ts-ignore - Type definitions need updating
    const takaraBoostInfo = calculation?.takaraBoostInfo
    let maxTakara = 0
    if (takaraBoostInfo && takaraBoostInfo.takaraNeededForFullBoost) {
      maxTakara = takaraBoostInfo.takaraNeededForFullBoost
    } else if (vault && takaraPrice > 0) {
      // Fallback: calculate locally using v3.0 formula
      const boostRange = (vault.maxAPY || 9.6) - (vault.baseAPY || 7.6)
      const fullBoostCostUSD = currentUsdtAmount * (boostRange / 100)
      maxTakara = fullBoostCostUSD / takaraPrice
    }

    return {
      laikaToUsdtRate: laikaRate,
      maxLaikaBoostUSD: boostUSD,
      maxLaikaMarketValueUSD: marketValueUSD,
      maxLaikaBoost: isFinite(maxLaika) ? maxLaika : 0,
      maxTakaraBoost: isFinite(maxTakara) ? maxTakara : 0
    }
  }, [calculation, laikaPriceResponse, usdtAmount, safeParseFloat, vault, takaraPrice])

  // Handle Set Max Boost button click - use current max value to prevent flicker
  const handleSetMaxBoost = useCallback(() => {
    const maxValue = boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost
    handleBoostAmountChange(maxValue)
  }, [boostToken, maxTakaraBoost, maxLaikaBoost, handleBoostAmountChange])

  if (vaultLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  if (!vault) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{t('vaults:detail.vaultNotFound')}</p>
          <button
            onClick={() => navigate('/vaults')}
            className="btn-outline-gold px-6 py-2 rounded-lg"
          >
            {t('vaults:detail.backToVaults')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/vaults')}
          className="flex items-center gap-2 text-gray-400 hover:text-gold-400 mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('vaults:detail.backToVaults')}
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Vault Info */}
          <div className="space-y-6">
            <div className="bg-background-card rounded-xl p-8 border border-green-900/20">
              <h1 className="text-3xl font-bold text-white mb-2">{vault.name}</h1>
              <p className="text-gray-400 mb-6">{vault.duration} {t('vaults:card.lockupPeriod')}</p>

              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-gold-500" />
                    <span className="text-sm text-gray-400">{t('vaults:card.baseAPY')}</span>
                  </div>
                  <div className="text-2xl font-bold text-gold-500">{vault.baseAPY}%</div>
                </div>

                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-gradient-gold" />
                    <span className="text-sm text-gray-400">{t('vaults:card.maxAPY')}</span>
                  </div>
                  <div className="text-2xl font-bold text-gradient-gold">{vault.maxAPY}%</div>
                </div>

                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins className="h-5 w-5 text-green-400" />
                    <span className="text-sm text-gray-400">{t('vaults:card.takaraAPY')}</span>
                  </div>
                  <div className="text-2xl font-bold text-green-400">
                    {vault.dynamicTakaraAPY ? `${vault.dynamicTakaraAPY}%` : 'Dynamic'}
                    <span className="text-sm text-gray-400 ml-2">{t('vaults:card.takaraAPYTVL')}</span>
                  </div>
                </div>

                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-5 w-5 text-teal-400" />
                    <span className="text-sm text-gray-400">{t('vaults:card.payouts')}</span>
                  </div>
                  <div className="text-sm font-medium text-white">
                    {vault.payoutSchedule.replace('_', ' ')}
                  </div>
                </div>
              </div>

              {/* Total Return Highlight */}
              <div className="mt-4 p-4 bg-gradient-to-r from-gold-500/20 to-gold-600/10 border border-gold-500/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">{t('vaults:detail.totalReturnMonths', { duration: vault.duration })}</div>
                    <div className="text-xs text-gray-500">{t('vaults:detail.totalReturnFormula')}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gold-400">
                      {(vault.baseAPY * vault.duration / 12).toFixed(1)}% → {(vault.maxAPY * vault.duration / 12).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Deactivation Countdown - hidden by request */}

              {/* Investment Range */}
              <div className="mt-6 p-4 bg-background-elevated rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-5 w-5 text-gold-500" />
                  <span className="text-sm font-medium text-gray-300">{t('vaults:detail.minimumAllocation')}</span>
                </div>
                <div className="flex flex-col space-y-1">
                  <div className="text-2xl font-bold text-white">
                    ${vault.minInvestment.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('vaults:detail.noMaximumLimit')}
                  </div>
                </div>
              </div>

              {/* TAKARA Requirement */}
              {vault.requireTAKARA && vault.takaraRatio && (
                <div className="mt-6 p-4 bg-green-900/10 border border-green-900/30 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-green-400 font-medium">
                      ⚡ {t('vaults:detail.takaraRequired')}
                    </div>
                    <div className="text-xs text-gold-400">
                      ${takaraPrice.toFixed(6)}/TAKARA
                    </div>
                  </div>
                  <div className="text-gray-300 text-sm">
                    <span className="font-semibold text-gold-400">{t('vaults:detail.takaraPerUSDT', { ratio: vault.takaraRatio })}</span>
                    <span className="text-gray-500 ml-2">(~${(vault.takaraRatio * takaraPrice).toFixed(4)})</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {t('vaults:detail.exampleInvestment', { amount: (vault.takaraRatio * 10).toLocaleString() })}
                    <span className="text-gold-400/70 ml-1">(~${(vault.takaraRatio * 10 * takaraPrice).toFixed(2)})</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Yield Calculator */}
          <div className="space-y-6">
            {/* 2-Step Payment Process Info */}
            <div className="bg-gradient-to-br from-teal-500/10 to-purple-500/10 border border-teal-500/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-10 w-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💳</span>
                </div>
                <h3 className="text-lg font-bold text-white">{t('vaults:detail.paymentProcess')}</h3>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-500 text-black rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <div className="font-semibold text-white">{t('vaults:detail.allPaymentsPhantom')}</div>
                    <div className="text-gray-400">
                      {t('vaults:detail.paymentDetails', {
                        requirement: vault.requireTAKARA ? t('vaults:detail.requirementTakara') : t('vaults:detail.requirementNoTakara')
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <CalculatorErrorBoundary>
            <div className="bg-background-card rounded-xl p-8 border border-green-900/20">
              <h2 className="text-2xl font-bold text-white mb-6">{t('vaults:detail.yieldCalculator')}</h2>

              {/* USDT Amount Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('vaults:detail.usdtAmount')}
                </label>
                <input
                  type="number"
                  value={usdtAmount}
                  onChange={(e) => setUsdtAmount(e.target.value)}
                  placeholder={t('vaults:detail.usdtAmountPlaceholder', { min: vault.minInvestment })}
                  min={vault.minInvestment}
                  className="w-full px-4 py-3 bg-background-elevated border border-green-900/30 focus:border-gold-500 rounded-lg text-white placeholder-gray-500 focus:outline-none"
                />
                {usdtAmount && currentParsedAmount < vault.minInvestment && currentParsedAmount > 0 && (
                  <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-400" dangerouslySetInnerHTML={{ __html: t('vaults:detail.minimumAllocationIs', { min: vault.minInvestment.toLocaleString() }) }} />
                  </div>
                )}
              </div>

              {/* Boost Token Selector */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-white mb-3 block">
                  {t('vaults:detail.chooseBoostToken')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setBoostToken('LAIKA')
                      setTakaraAmount(0)  // Reset TAKARA when switching to LAIKA
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      boostToken === 'LAIKA'
                        ? 'bg-laika-purple/20 border-laika-purple shadow-lg shadow-laika-purple/20'
                        : 'bg-background-elevated border-gray-700 hover:border-laika-purple/50'
                    }`}
                  >
                    <div className="text-lg font-bold text-laika-purple mb-1">🐕 LAIKA</div>
                    <div className="text-xs text-gray-400">{t('vaults:detail.cosmodogCommunityToken')}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBoostToken('TAKARA')
                      setLaikaAmount(0)  // Reset LAIKA when switching to TAKARA
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      boostToken === 'TAKARA'
                        ? 'bg-gold/20 border-gold shadow-lg shadow-gold/20'
                        : 'bg-background-elevated border-gray-700 hover:border-gold/50'
                    }`}
                  >
                    <div className="text-lg font-bold text-gold mb-1">💎 TAKARA</div>
                    <div className="text-xs text-gray-400">{t('vaults:detail.platformNativeToken', { price: `$${takaraPrice.toFixed(6)}` })}</div>
                  </button>
                </div>
              </div>

              {/* Boost Amount Input */}
              <div className="mb-6">
                <div className={`border rounded-lg p-4 ${
                  boostToken === 'LAIKA'
                    ? 'bg-gradient-laika/10 border-laika-purple/30'
                    : 'bg-gold/5 border-gold/30'
                }`}>
                  <div className="flex justify-between items-center mb-3">
                    <label className={`text-sm font-bold flex items-center gap-2 ${
                      boostToken === 'LAIKA' ? 'text-laika-purple' : 'text-gold'
                    }`}>
                      <span className="text-lg">🚀</span>
                      {t('vaults:detail.boostOptional', { token: boostToken })}
                    </label>
                    <div className="text-right">
                      <span className={`text-lg font-bold block ${
                        boostToken === 'LAIKA' ? 'text-laika-purple' : 'text-gold'
                      }`}>
                        {currentBoostAmount.toLocaleString()} {boostToken}
                      </span>
                      <span className="text-xs text-gray-400">
                        ≈ ${(currentBoostAmount * (boostToken === 'LAIKA' ? (calculation?.investment?.laikaPrice || laikaToUsdtRate || 0) : (takaraPrice || 0))).toFixed(2)} USDT
                      </span>
                    </div>
                  </div>

                  {/* Input Field */}
                  <div className="mb-3">
                    <label className="block text-xs text-gray-400 mb-2">
                      {t('vaults:detail.boostAmountLabel', {
                        token: boostToken,
                        max: (boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost).toLocaleString(undefined, {maximumFractionDigits: 2})
                      })}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost}
                      step={boostToken === 'TAKARA' ? '1' : '100'}
                      value={currentBoostAmount || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0
                        const maxValue = boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost
                        handleBoostAmountChange(Math.min(value, maxValue))
                      }}
                      placeholder={t('vaults:detail.enterBoostAmount', { token: boostToken })}
                      disabled={!usdtAmount || currentParsedAmount === 0}
                      className={`w-full px-4 py-3 bg-background-elevated border rounded-lg text-white placeholder-gray-500 focus:outline-none ${
                        boostToken === 'LAIKA'
                          ? 'border-laika-purple/30 focus:border-laika-purple'
                          : 'border-gold/30 focus:border-gold'
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('vaults:detail.enterBoostHelp', {
                        token: boostToken,
                        max: (boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost).toLocaleString(undefined, {maximumFractionDigits: 2})
                      })}
                    </p>

                    {/* Max Boost Button */}
                    <button
                      onClick={handleSetMaxBoost}
                      disabled={!usdtAmount || currentParsedAmount === 0}
                      className={`mt-2 w-full py-2.5 rounded-lg font-semibold transition-all text-sm ${
                        boostToken === 'LAIKA'
                          ? 'bg-laika-purple hover:bg-laika-purple/80 text-white'
                          : 'bg-gold-500 hover:bg-gold-400 text-black'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      🚀 {t('vaults:detail.setMaxBoost', {
                        token: boostToken,
                        amount: (boostToken === 'TAKARA' ? maxTakaraBoost : maxLaikaBoost).toLocaleString(undefined, {maximumFractionDigits: 2})
                      })}
                    </button>
                  </div>

                  {/* Boost Preview */}
                  {currentBoostAmount > 0 && calculation && (
                    <div className="bg-black/20 rounded p-3 space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{t('vaults:detail.boostValue', { token: boostToken })}</span>
                        <span className={`font-bold text-sm ${
                          boostToken === 'LAIKA' ? 'text-laika-purple' : 'text-gold'
                        }`}>
                          ${boostToken === 'LAIKA'
                            ? (calculation.investment.laikaBoostValueUSD?.toFixed(2) || '0.00')
                            : ((currentBoostAmount * (takaraPrice || 0.001506)).toFixed(2))
                          } USDT
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{t('vaults:detail.tokenPrice', { token: boostToken })}</span>
                        <span className="text-white font-medium text-sm flex items-center gap-2">
                          {laikaPriceFetching ? (
                            <span className="animate-pulse">{t('vaults:detail.updating')}</span>
                          ) : (
                            <>${boostToken === 'LAIKA'
                              ? (calculation?.investment?.laikaPrice || laikaToUsdtRate || 0.0000007).toFixed(8)
                              : (takaraPrice || 0.001506).toFixed(6)
                            } USDT</>
                          )}
                          {boostToken === 'LAIKA' && (
                            <button
                              onClick={() => refetchLaikaPrice()}
                              className="text-xs text-laika-purple hover:text-laika-green transition-colors"
                              title={t('vaults:detail.refreshPrice')}
                            >
                              🔄
                            </button>
                          )}
                        </span>
                      </div>
                      {boostToken === 'LAIKA' && (
                        <div className="mt-2 p-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                          <div className="text-xs text-purple-300 text-center font-bold mb-1">
                            🐕 {t('vaults:detail.laikaBoostTitle')}
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">{t('vaults:detail.marketPrice')}</span>
                            <span className="text-purple-400 font-bold">${(calculation?.investment?.laikaPrice || laikaToUsdtRate).toFixed(8)}</span>
                          </div>
                        </div>
                      )}
                      {boostToken === 'TAKARA' && (
                        <div className="mt-2 p-2 bg-gradient-to-r from-gold/10 to-yellow-500/10 border border-gold/30 rounded-lg">
                          <div className="text-xs text-gold text-center font-bold mb-1">
                            💎 {t('vaults:detail.takaraDynamicPricing')}
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">{t('vaults:detail.currentPrice')}</span>
                            <span className="text-gold font-bold">${(takaraPrice || 0.001506).toFixed(6)}</span>
                          </div>
                          <div className="text-xs text-gray-500 text-center mt-1">
                            {t('vaults:detail.priceDynamicInfo')}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between items-center border-t border-gray-700 pt-2">
                        <span className="text-laika-green font-bold">{t('vaults:detail.extraAPY')}</span>
                        <span className="text-laika-green font-bold text-sm">
                          +{calculation.earnings.laikaBoostAPY || 0}%
                        </span>
                      </div>
                    </div>
                  )}

                  {!usdtAmount || currentParsedAmount === 0 ? (
                    <div className="text-xs text-gray-500 italic mt-2">
                      💡 {t('vaults:detail.enterUSDTFirst', { token: boostToken })}
                    </div>
                  ) : currentBoostAmount === 0 ? (
                    <div className="text-xs text-gray-400 italic mt-2">
                      💡 {t('vaults:detail.enterBoostToIncrease', { token: boostToken })}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Calculation Results */}
              {calculating && (
                <div className="text-center py-8">
                  <div className="loading-spinner mx-auto mb-2"></div>
                  <p className="text-sm text-gray-400">{t('vaults:detail.calculating')}</p>
                </div>
              )}

              {/* Show error if calculation failed */}
              {calculateError && !calculating && currentParsedAmount > 0 && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg mb-4">
                  <p className="text-sm text-red-400">
                    {t('vaults:detail.calculationError')}
                  </p>
                </div>
              )}

              {calculation && !calculating && (
                <div className="space-y-4">
                  {/* Final APY */}
                  <div className="p-4 bg-gradient-to-br from-gold-500/10 to-transparent border border-gold-500/30 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">{t('vaults:detail.yourFinalAPY')}</div>
                    <div className="text-3xl font-bold text-gradient-gold">
                      {calculation.earnings.finalAPY}%
                    </div>
                    {calculation.earnings.laikaBoostAPY > 0 && (
                      <div className="text-sm text-green-400 mt-1">
                        {t('vaults:detail.fromBoost', { apy: calculation.earnings.laikaBoostAPY, token: boostToken })}
                      </div>
                    )}
                  </div>

                  {/* Earnings Summary */}
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-background-elevated rounded-lg">
                      <span className="text-gray-400">{t('vaults:detail.totalUSDTEarnings')}</span>
                      <span className="text-white font-semibold">
                        ${calculation.earnings.totalUSDT.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-background-elevated rounded-lg">
                      <span className="text-gray-400">{t('vaults:detail.payoutAmount')}</span>
                      <span className="text-white font-semibold">
                        ${calculation.earnings.payoutAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-background-elevated rounded-lg">
                      <span className="text-gray-400">{t('vaults:detail.numberOfPayouts')}</span>
                      <span className="text-white font-semibold">
                        {calculation.earnings.numberOfPayouts}
                      </span>
                    </div>
                  </div>

                  {/* ROI */}
                  <div className="p-4 bg-gold-500/10 border border-gold-500/30 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">{t('vaults:detail.totalROI')}</div>
                    <div className="text-2xl font-bold text-gold-500">
                      {calculation.summary.roi}
                    </div>
                  </div>

                  {/* Capacity Warning - v3.3.2: Only show if totalCapacity is explicitly set */}
                  {vault && vault.effectiveCapacity && vault.effectiveCapacity > 0 && vault.remainingCapacity !== undefined && (
                    <div className={`mb-4 p-3 rounded-lg border ${
                      vault.capacityPercentage && vault.capacityPercentage >= 90
                        ? 'bg-red-500/10 border-red-500/30'
                        : vault.capacityPercentage && vault.capacityPercentage >= 75
                        ? 'bg-orange-500/10 border-orange-500/30'
                        : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-300">{t('vaults:detail.vaultCapacity')}</span>
                        <span className={`text-xs font-bold ${
                          vault.capacityPercentage && vault.capacityPercentage >= 90
                            ? 'text-red-400'
                            : vault.capacityPercentage && vault.capacityPercentage >= 75
                            ? 'text-orange-400'
                            : 'text-yellow-400'
                        }`}>
                          {vault.capacityPercentage?.toFixed(1)}% {t('vaults:detail.filled')}
                        </span>
                      </div>
                      <div className="relative h-2 bg-navy-900 rounded-full overflow-hidden mb-2">
                        <div
                          className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                            vault.capacityPercentage && vault.capacityPercentage >= 90
                              ? 'bg-red-500'
                              : vault.capacityPercentage && vault.capacityPercentage >= 75
                              ? 'bg-orange-500'
                              : 'bg-yellow-500'
                          }`}
                          style={{
                            width: `${Math.min(vault.capacityPercentage || 0, 100)}%`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Invest Button */}
                  {!isAuthenticated ? (
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="btn-gold w-full py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
                    >
                      <LogIn className="h-5 w-5" />
                      {t('vaults:detail.loginToInvest')}
                    </button>
                  ) : !connected ? (
                    <div className="text-center py-4 text-gray-400">
                      {t('vaults:detail.connectWallet')}
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsModalOpen(true)}
                      disabled={
                        !isVaultPurchasingEnabled ||
                        (vault && vault.effectiveCapacity && vault.effectiveCapacity > 0 && vault.remainingCapacity !== undefined && vault.remainingCapacity < currentParsedAmount && currentParsedAmount > 0)
                      }
                      className={`btn-gold w-full py-4 rounded-lg font-semibold text-lg ${
                        !isVaultPurchasingEnabled ||
                        (vault && vault.effectiveCapacity && vault.effectiveCapacity > 0 && vault.remainingCapacity !== undefined && vault.remainingCapacity < currentParsedAmount && currentParsedAmount > 0)
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      {!isVaultPurchasingEnabled ? (
                        launchDate ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-sm font-medium">{t('vaults:comingSoon')}</span>
                            <span className="text-base font-bold">
                              {countdown.days > 0 && `${countdown.days}d `}
                              {String(countdown.hours).padStart(2, '0')}:
                              {String(countdown.minutes).padStart(2, '0')}:
                              {String(countdown.seconds).padStart(2, '0')}
                            </span>
                          </div>
                        ) : (
                          t('vaults:comingSoon')
                        )
                      ) : vault && vault.effectiveCapacity && vault.effectiveCapacity > 0 && vault.remainingCapacity !== undefined && vault.remainingCapacity < currentParsedAmount && currentParsedAmount > 0 ? (
                        t('vaults:detail.insufficientCapacity')
                      ) : (
                        t('vaults:detail.investNow')
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            </CalculatorErrorBoundary>
          </div>
        </div>
      </div>

      {/* Investment Modal */}
      {calculation && (
        <InvestmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          vaultId={id!}
          calculation={calculation}
          usdtAmount={currentParsedAmount}
          laikaAmount={boostToken === 'LAIKA' ? laikaAmount : 0}
          takaraAmount={boostToken === 'TAKARA' ? takaraAmount : 0}
          takaraPrice={takaraPrice}
          boostType={boostToken}
          acceptedPayments={vault?.acceptedPayments || 'USDT,TRX'}
        />
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['currentUser'] })
        }}
      />
    </div>
  )
}

/**
 * Vault Countdown Component
 * Displays remaining time until vault deactivation
 */
function VaultCountdown({ countdownSeconds }: { countdownSeconds: number }) {
  const [remaining, setRemaining] = useState(countdownSeconds)

  useEffect(() => {
    setRemaining(countdownSeconds)
    
    if (countdownSeconds <= 0) return

    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [countdownSeconds])

  const days = Math.floor(remaining / 86400)
  const hours = Math.floor((remaining % 86400) / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60

  if (remaining <= 0) {
    return (
      <div className="text-right">
        <div className="text-lg font-bold text-red-400">CLOSED</div>
        <div className="text-xs text-gray-400">No new investments</div>
      </div>
    )
  }

  const timeUnit = days > 0 ? 'day' : hours > 0 ? 'hour' : 'min'
  const timeValue = days > 0 ? days : hours > 0 ? hours : minutes

  return (
    <div className="text-right">
      <div className="text-2xl font-bold text-orange-400">
        {days > 0 && `${days}d `}
        {hours.toString().padStart(2, '0')}:
        {minutes.toString().padStart(2, '0')}:
        {seconds.toString().padStart(2, '0')}
      </div>
      <div className="text-xs text-gray-400">
        {timeValue} {timeUnit}{timeValue > 1 ? 's' : ''} remaining
      </div>
    </div>
  )
}
